import { useEffect, useState } from 'react'

const GLYPH_FRAMES = ['·', '✢', '✳', '✶', '✻', '✶', '✳', '✢']
const GLYPH_INTERVAL_MS = 120
const WORD_INTERVAL_MS = 4000
const REDUCED_MOTION_GLYPH = '✳'

// Longest word must stay ≤ 13 chars — the word span reserves a 14ch slot (word + ellipsis) so
// rotation never reflows the row.
const SCAN_WORDS = [
  'Clickclacking',
  'Noodling',
  'Percolating',
  'Marinating',
  'Reticulating',
  'Whirring',
  'Cogitating',
  'Ruminating',
  'Pondering',
  'Mulling',
  'Brewing',
  'Simmering',
  'Puttering',
  'Finagling',
  'Wrangling',
  'Conjuring',
  'Crunching',
  'Moseying',
  'Schlepping',
  'Tinkering',
]

function shuffle(words: readonly string[]): string[] {
  const shuffled = [...words]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

function formatElapsed(totalSeconds: number): string {
  if (totalSeconds < 60) {return `(${totalSeconds}s)`}

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `(${minutes}m ${seconds}s)`
}

function secondsSince(startedAtMs: number): number {
  return Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000))
}

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handleChange = (event: MediaQueryListEvent) => setPrefersReducedMotion(event.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  return prefersReducedMotion
}

interface ScanningIndicatorProps {
  startedAtMs?: number
  label?: string
}

export function ScanningIndicator({ startedAtMs, label = 'Scanning' }: ScanningIndicatorProps) {
  const [mountedAtMs] = useState(() => Date.now())
  const clockStartMs = startedAtMs ?? mountedAtMs
  const [words] = useState(() => shuffle(SCAN_WORDS))
  const [frameIndex, setFrameIndex] = useState(0)
  const [wordIndex, setWordIndex] = useState(0)
  const [elapsedSeconds, setElapsedSeconds] = useState(() => secondsSince(clockStartMs))
  const prefersReducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    if (prefersReducedMotion) {return}

    const interval = setInterval(
      () => setFrameIndex((index) => (index + 1) % GLYPH_FRAMES.length),
      GLYPH_INTERVAL_MS,
    )

    return () => clearInterval(interval)
  }, [prefersReducedMotion])

  useEffect(() => {
    if (prefersReducedMotion) {return}

    const interval = setInterval(
      () => setWordIndex((index) => (index + 1) % words.length),
      WORD_INTERVAL_MS,
    )

    return () => clearInterval(interval)
  }, [prefersReducedMotion, words.length])

  useEffect(() => {
    const interval = setInterval(() => setElapsedSeconds(secondsSince(clockStartMs)), 1000)

    return () => clearInterval(interval)
  }, [clockStartMs])

  const glyph = prefersReducedMotion ? REDUCED_MOTION_GLYPH : GLYPH_FRAMES[frameIndex]
  const word = words[wordIndex]

  return (
    <span className="flex items-center gap-3">
      <span aria-hidden className="inline-block w-[1.1em] text-center font-mono text-[13px] leading-none text-primary">
        {glyph}
      </span>
      <span aria-hidden className="font-mono text-[11px] uppercase tracking-eyebrow text-primary">
        <span key={word} className="inline-block min-w-[14ch] animate-in fade-in duration-150 motion-reduce:animate-none">
          {word}…
        </span>{' '}
        <span className="text-muted-foreground">{formatElapsed(elapsedSeconds)}</span>
      </span>
      {/* Mounted with its content, some SR/browser pairs skip the initial announcement — acceptable; consumers needing guaranteed announcement should own a persistent status container. */}
      <span className="sr-only" role="status">
        {label}
      </span>
    </span>
  )
}
